/*
  React Flow Node Converter

  This module handles converting document model nodes to React Flow format.
*/

import type { Node as ReactFlowNode } from 'react-flow-renderer';
import type { Node as DocNode } from '../../types';
import { NODE_TYPE_LABELS } from './constants';

/**
 * Get a human-readable label for a node type
 *
 * @param type - The node type
 * @returns A human-readable label
 */
export function getNodeTypeLabel(type: string): string {
  return NODE_TYPE_LABELS[type] || type.charAt(0).toUpperCase() + type.slice(1);
}

/**
 * Convert a document node to React Flow node format
 *
 * This function creates a React Flow node with:
 * - Proper type mapping
 * - Default position (will be set by layout engine)
 * - Node data including label and tooltip
 * - Draggable and selectable flags
 *
 * @param docNode - The document node to convert
 * @returns A React Flow node
 */
export function convertDocNodeToReactFlowNode(docNode: DocNode): ReactFlowNode {
  return {
    id: docNode.id,
    type: docNode.type,
    position: { x: 0, y: 0 }, // Will be set by layout engine
    data: {
      ...docNode,
      label: docNode.name || getNodeTypeLabel(docNode.type),
      tooltip: docNode.name || getNodeTypeLabel(docNode.type), // For hover tooltips
    },
    draggable: true,
    selectable: true,
    // No custom styles - let the node components handle all styling
  };
}

